// Building Data Service
// Aggregates building construction dates from multiple municipal sources
// Priority: Tel Aviv GIS > Jerusalem GIS > CBS data > Enhanced estimation

import config from '../config/api.js';

/**
 * Service to fetch and aggregate building construction data from multiple sources
 */
class BuildingDataService {
  
  /**
   * Get building construction data for a specific address
   * @param {string} address - Full address
   * @param {Object} coordinates - {x, y} coordinates in ITM/Israeli Grid
   * @param {string} municipalityName - Municipality name
   * @returns {Promise<Object>} Building construction data
   */
  static async getBuildingConstructionData(address, coordinates, municipalityName) {
    console.log(`🏗️ Fetching building construction data for: ${address} in ${municipalityName}`);

    try {
      // Try municipal-specific sources first
      let buildingData = null;

      if (municipalityName === 'תל אביב-יפו' && coordinates) {
        buildingData = await this.getTelAvivBuildingData(address, coordinates);
      } else if (municipalityName === 'ירושלים' && coordinates) {
        buildingData = await this.getJerusalemBuildingData(address, coordinates);
      }

      // If no municipal data found, try enhanced estimation
      if (!buildingData || !buildingData.found) {
        console.log(`⚠️ No municipal building data found, using enhanced estimation`);
        buildingData = await this.getEnhancedEstimation(address, municipalityName, coordinates);
      }

      // Validate and enrich the data
      return this.validateAndEnrichBuildingData(buildingData, address, municipalityName);

    } catch (error) {
      console.error(`❌ Error fetching building data for ${address}:`, error);
      return this.getFallbackBuildingData(address, municipalityName);
    }
  }

  /**
   * Get Tel Aviv building construction data from GIS permits database
   * @param {string} address - Address
   * @param {Object} coordinates - {x, y} coordinates
   * @returns {Promise<Object>} Tel Aviv building data
   */
  static async getTelAvivBuildingData(address, coordinates) {
    try {
      console.log(`🏛️ Fetching Tel Aviv building permits data for: ${address}`);

      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

      const response = await fetch(
        `${config.apiBaseUrl}/api/building-permits?x=${coordinates.x}&y=${coordinates.y}&address=${encodeURIComponent(address)}`,
        {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.log(`⚠️ Tel Aviv building permits API failed with status: ${response.status}`);
        return null;
      }

      const data = await response.json();
      
      if (data.success && data.buildingData.found) {
        console.log(`✅ Tel Aviv building data found: Construction year ${data.buildingData.constructionYear}`);
        return {
          found: true,
          constructionYear: data.buildingData.constructionYear,
          pre1980Eligible: data.buildingData.pre1980Eligible,
          permits: data.buildingData.permits,
          tama38Status: data.buildingData.tama38Status,
          confidence: data.buildingData.confidence,
          dataSource: 'tel_aviv_gis',
          municipality: 'תל אביב-יפו'
        };
      }

      return null;

    } catch (error) {
      console.error('❌ Error fetching Tel Aviv building data:', error);
      return null;
    }
  }

  /**
   * Get Jerusalem building construction data (placeholder for future implementation)
   * @param {string} address - Address
   * @param {Object} coordinates - {x, y} coordinates
   * @returns {Promise<Object>} Jerusalem building data
   */
  static async getJerusalemBuildingData(address, coordinates) {
    // TODO: Implement Jerusalem municipal GIS integration
    console.log(`🏛️ Jerusalem building data integration - TODO: Implement`);
    return null;
  }

  /**
   * Enhanced estimation using multiple indicators
   * @param {string} address - Address
   * @param {string} municipalityName - Municipality
   * @param {Object} coordinates - Coordinates if available
   * @returns {Promise<Object>} Estimated building data
   */
  static async getEnhancedEstimation(address, municipalityName, coordinates) {
    console.log(`🔍 Running enhanced building age estimation for: ${address}`);

    try {
      // Analyze street name and number patterns
      const streetAnalysis = this.analyzeStreetDevelopmentEra(address);
      
      // Get neighborhood development patterns from CBS (future enhancement)
      const neighborhoodPatterns = await this.getNeighborhoodConstructionPatterns(municipalityName, address);
      
      // Combine indicators for estimation
      const estimatedYear = this.combineEstimationIndicators(streetAnalysis, neighborhoodPatterns);
      
      return {
        found: true,
        constructionYear: estimatedYear,
        pre1980Eligible: estimatedYear ? estimatedYear < 1980 : null,
        confidence: 'estimated_low',
        dataSource: 'enhanced_estimation',
        municipality: municipalityName,
        estimationFactors: {
          streetAnalysis,
          neighborhoodPatterns
        }
      };

    } catch (error) {
      console.error('❌ Enhanced estimation error:', error);
      return null;
    }
  }

  /**
   * Analyze street name and development patterns to estimate construction era
   * @param {string} address - Full address
   * @returns {Object} Street analysis results
   */
  static analyzeStreetDevelopmentEra(address) {
    const streetName = address.split(',')[0].trim();
    
    // Historical street naming patterns in Israel
    const eraIndicators = {
      pre1948: ['הרצל', 'ביאליק', 'אחד העם', 'מונטיפיורי', 'רוטשילד'],
      british1920s1940s: ['אלנבי', 'קינג ג\'ורג\'', 'המלך ג\'ורג\'', 'הרצל'],
      early1950s: ['בן גוריון', 'ויצמן', 'שרת', 'ז\'בוטינסקי'],
      modernEra: ['אינשטיין', 'רבין', 'בגין', 'פרס']
    };

    let likelyEra = 'unknown';
    let estimatedDecade = null;

    for (const [era, patterns] of Object.entries(eraIndicators)) {
      if (patterns.some(pattern => streetName.includes(pattern))) {
        likelyEra = era;
        switch (era) {
          case 'pre1948': estimatedDecade = 1930; break;
          case 'british1920s1940s': estimatedDecade = 1940; break;
          case 'early1950s': estimatedDecade = 1950; break;
          case 'modernEra': estimatedDecade = 1970; break;
          default: estimatedDecade = null; break;
        }
        break;
      }
    }

    return {
      streetName,
      likelyEra,
      estimatedDecade,
      confidence: estimatedDecade ? 'low' : 'none'
    };
  }

  /**
   * Get neighborhood construction patterns from CBS data (future enhancement)
   * @param {string} municipalityName - Municipality
   * @param {string} address - Address
   * @returns {Promise<Object>} Neighborhood patterns
   */
  static async getNeighborhoodConstructionPatterns(municipalityName, address) {
    // TODO: Integrate with CBS API for neighborhood construction statistics
    console.log(`📊 CBS neighborhood patterns integration - TODO: Implement`);
    return {
      found: false,
      averageConstructionYear: null,
      confidence: 'none'
    };
  }

  /**
   * Combine multiple estimation indicators
   * @param {Object} streetAnalysis - Street analysis results
   * @param {Object} neighborhoodPatterns - Neighborhood patterns
   * @returns {number|null} Estimated construction year
   */
  static combineEstimationIndicators(streetAnalysis, neighborhoodPatterns) {
    const indicators = [];

    if (streetAnalysis.estimatedDecade) {
      indicators.push({
        year: streetAnalysis.estimatedDecade,
        weight: 0.3,
        source: 'street_analysis'
      });
    }

    if (neighborhoodPatterns.averageConstructionYear) {
      indicators.push({
        year: neighborhoodPatterns.averageConstructionYear,
        weight: 0.7,
        source: 'neighborhood_patterns'
      });
    }

    if (indicators.length === 0) return null;

    // Weighted average
    const totalWeight = indicators.reduce((sum, ind) => sum + ind.weight, 0);
    const weightedSum = indicators.reduce((sum, ind) => sum + (ind.year * ind.weight), 0);
    
    return Math.round(weightedSum / totalWeight);
  }

  /**
   * Validate and enrich building data
   * @param {Object} buildingData - Raw building data
   * @param {string} address - Address
   * @param {string} municipalityName - Municipality
   * @returns {Object} Validated and enriched data
   */
  static validateAndEnrichBuildingData(buildingData, address, municipalityName) {
    if (!buildingData) {
      return this.getFallbackBuildingData(address, municipalityName);
    }

    // Validate construction year
    const currentYear = new Date().getFullYear();
    if (buildingData.constructionYear) {
      if (buildingData.constructionYear < 1900 || buildingData.constructionYear > currentYear) {
        console.warn(`⚠️ Invalid construction year: ${buildingData.constructionYear}, using fallback`);
        buildingData.constructionYear = null;
        buildingData.confidence = 'invalid_data';
      }
    }

    // Ensure pre1980Eligible is calculated
    if (buildingData.constructionYear && buildingData.pre1980Eligible === null) {
      buildingData.pre1980Eligible = buildingData.constructionYear < 1980;
    }

    // Add metadata
    buildingData.address = address;
    buildingData.municipality = municipalityName;
    buildingData.fetchedAt = new Date().toISOString();

    return buildingData;
  }

  /**
   * Fallback building data when no sources are available
   * @param {string} address - Address
   * @param {string} municipalityName - Municipality
   * @returns {Object} Fallback data
   */
  static getFallbackBuildingData(address, municipalityName) {
    return {
      found: false,
      constructionYear: null,
      pre1980Eligible: null,
      confidence: 'no_data',
      dataSource: 'fallback',
      municipality: municipalityName,
      address: address,
      fetchedAt: new Date().toISOString(),
      note: 'No building construction data available from municipal sources'
    };
  }

  /**
   * Get supported municipalities for building data
   * @returns {Array<string>} List of supported municipalities
   */
  static getSupportedMunicipalities() {
    return [
      'תל אביב-יפו', // Full Tel Aviv GIS integration
      'ירושלים'      // Future Jerusalem integration
    ];
  }
}

export default BuildingDataService;
